import * as React from "react";
import { useWindowManager } from "../../windowManager/WindowManagerContext";
import { openLinkedMailWindow } from "../LinkedMailWindow";
import { Email, graphMailService } from "../../../services/graphMailService";
import { mailboxChipLabel } from "../mailSearch";
import {
    buildParticipantsQuery,
    historieAdressen,
    mergeThreadHits,
    type SidebarThreadHit,
} from "../mailContactHistory";
import { mailLinkService } from "../../../services/mailLinkService";
import { emailAusMailLink } from "../../kommunikationHelpers";
import { Chip, Fehlerzeile, Hinweis, Laedt, Zeile } from "./sidebarChrome";
import { INK, INK2 } from "../../chat/chatTheme";
import { reportMailError } from "../../../services/mailError";

/**
 * Reiter „Schriftwechsel" der rechten Mail-Leiste (C1).
 *
 * Verlauf ALLER Mails von/an die Adressmenge der geöffneten Mail (empfangen +
 * gesendet), chronologisch über alle lesbaren Postfächer, je Treffer mit
 * Postfach-Chip. Funktioniert unabhängig von einer KMVS-Zuweisung.
 *
 * Eigener Lade-/Fehlerzustand: früher teilten sich Schriftwechsel, Termine und
 * Vorgänge einen einzigen — ein Netzfehler in einem Reiter machte alle drei
 * unbrauchbar.
 */

const fmtDate = (d: Date): string => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export type SchriftwechselTabProps = {
    /** Aktiv sichtbar? Erst dann wird geladen (die Leiste ist standardmäßig zu). */
    aktiv: boolean;
    /** Der ZUGEWIESENE Kontakt der Mail (J2) — er bestimmt die Adressmenge. */
    contactId: number | null;
    /** Anzeigename für die Rückfall-Hinweiszeile. */
    contactName?: string | null;
    /** Adresse(n) der geöffneten Mail (i. d. R. der Absender). */
    mailAddresses?: string[];
    /** Alle lesbaren Postfächer — die Suche läuft über alle (Entscheidung 21). */
    readableMailboxes: string[];
    /** Klick auf einen Treffer. Ohne Callback ist die Liste nicht klickbar. */
    onOpenMail?: (mailbox: string, email: Email) => void;
    /**
     * J1 — die GERADE geöffnete Mail: sie wird als „· geöffnet" markiert und ist
     * nicht anklickbar. Sonst navigierte ein Klick auf sich selbst (zweite
     * Schleifen-Bremse neben `navPush`).
     */
    offeneMail?: { mailbox: string; messageId: string } | null;
};

export const SchriftwechselTab: React.FC<SchriftwechselTabProps> = ({
    aktiv,
    contactId,
    contactName,
    mailAddresses,
    readableMailboxes,
    onOpenMail,
    offeneMail,
}) => {
    const { openWindow } = useWindowManager();
    // null = noch nicht geladen; Array (auch leer) = geladen.
    const [treffer, setTreffer] = React.useState<SidebarThreadHit[] | null>(null);
    const [laedt, setLaedt] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [versuch, setVersuch] = React.useState(0);
    /** J2 — der Kontakt hat keine hinterlegte Adresse; gezeigt wird ersatzweise
     *  der Verlauf zur Absenderadresse. Wird als Zeile erklärt. */
    const [rueckfallAuf, setRueckfallAuf] = React.useState<string | null>(null);

    // Aus dem INHALT abgeleitet (nicht der Array-Referenz), damit kein Fetch-Loop.
    const adressKey = React.useMemo(
        () =>
            `${contactId ?? ""}|${[...(mailAddresses ?? [])]
                .map((a) => String(a ?? "").trim().toLowerCase())
                .filter(Boolean)
                .sort()
                .join(",")}`,
        [contactId, mailAddresses],
    );

    React.useEffect(() => {
        setTreffer(null);
        setFehler(null);
    }, [adressKey]);

    React.useEffect(() => {
        if (!aktiv || treffer !== null) return;
        let abgebrochen = false;
        const run = async () => {
            setLaedt(true);
            setFehler(null);
            try {
                const kontaktAdressen =
                    contactId != null ? await graphMailService.fetchContactMailAddresses(contactId) : [];
                const { adressen: use, rueckfall } = historieAdressen(
                    kontaktAdressen,
                    mailAddresses,
                    contactId != null,
                );
                if (!abgebrochen) setRueckfallAuf(rueckfall ? (use[0] ?? null) : null);

                /**
                 * ZWEITE Quelle (J2/F1): die beim Kontakt persistierten
                 * Verknüpfungen. Sie zeigen auch dem, der das Postfach gar
                 * nicht lesen darf, den relevanten Schriftwechsel — die reine
                 * Graph-Suche liefert für ihn eine leere Liste. Der Aufruf ist
                 * serverseitig gegatet und liefert im Zweifel leer, nie 403.
                 */
                const ausVerknuepfungen: SidebarThreadHit[] = [];
                if (contactId != null) {
                    try {
                        for (const l of await mailLinkService.listByContact(contactId)) {
                            ausVerknuepfungen.push({
                                ...emailAusMailLink(l),
                                srcMailbox: l.mailbox,
                                link: l,
                            } as SidebarThreadHit);
                        }
                    } catch (error) {
                        reportMailError({
                            area: "mail",
                            action: "load-linked-mails",
                            error,
                            fallbackMessage: "Verknüpfte Mails konnten nicht geladen werden.",
                        });
                    }
                }

                const kql = buildParticipantsQuery(use);
                const jePostfach = kql
                    ? await Promise.all(
                          readableMailboxes.map(async (mb) => {
                              try {
                                  const items = await graphMailService.searchMail(mb, kql, 50);
                                  return items.map((e) => ({ ...e, srcMailbox: mb }) as SidebarThreadHit);
                              } catch (error) {
                                  reportMailError({
                                      area: "mail",
                                      action: "search",
                                      error,
                                      fallbackMessage: "Der Schriftwechsel konnte nicht vollständig geladen werden.",
                                  });
                                  return [] as SidebarThreadHit[];
                              }
                          }),
                      )
                    : [];
                // Dedupe über (Postfach, messageId) — die Graph-Treffer stehen
                // zuerst, sie tragen den vollständigeren Datensatz.
                if (!abgebrochen)
                    setTreffer(mergeThreadHits([...jePostfach.flat(), ...ausVerknuepfungen]));
            } catch (error) {
                const message = reportMailError({
                    area: "mail",
                    action: "load-linked-mails",
                    error,
                    fallbackMessage: "Schriftwechsel konnte nicht geladen werden.",
                });
                if (!abgebrochen) setFehler(message);
            } finally {
                if (!abgebrochen) setLaedt(false);
            }
        };
        void run();
        return () => {
            abgebrochen = true;
        };
        // `treffer` bewusst ausgeklammert (Null-Guard oben verhindert Re-Fetch).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, adressKey, versuch]);

    if (laedt && treffer === null) return <Laedt />;
    if (fehler)
        return (
            <Fehlerzeile
                text={fehler}
                onRetry={() => {
                    setFehler(null);
                    setVersuch((v) => v + 1);
                }}
            />
        );

    const liste = treffer ?? [];
    const rueckfallZeile = rueckfallAuf ? (
        <div style={{ fontSize: 12, color: INK2, marginBottom: 8, lineHeight: 1.4 }}>
            Für {contactName?.trim() || "diesen Kontakt"} ist keine E-Mail-Adresse hinterlegt — gezeigt
            wird der Verlauf zu {rueckfallAuf}.
        </div>
    ) : null;
    if (liste.length === 0)
        return (
            <>
                {rueckfallZeile}
                <Hinweis>Kein Schriftwechsel gefunden.</Hinweis>
            </>
        );

    const istOffen = (mb: string, id: string): boolean =>
        Boolean(
            offeneMail &&
                offeneMail.messageId === id &&
                offeneMail.mailbox.trim().toLowerCase() === mb.trim().toLowerCase(),
        );

    return (
        <>
            {rueckfallZeile}
            {liste.map((m) => {
                const offen = istOffen(m.srcMailbox, m.id);
                return (
                <Zeile
                    key={`${m.srcMailbox}:${m.id}`}
                    inaktiv={offen}
                    titel={offen ? "Diese Mail ist gerade geöffnet." : undefined}
                    onClick={m.link ? () => openLinkedMailWindow(openWindow, m.link!) : onOpenMail ? () => onOpenMail(m.srcMailbox, m) : undefined}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                        <span
                            style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: INK,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {m.senderName || m.senderEmail || "Unbekannt"}
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            {offen && <span style={{ fontSize: 11, color: INK2 }}>· geöffnet</span>}
                            <Chip>{mailboxChipLabel(m.srcMailbox)}</Chip>
                            <span style={{ fontSize: 11, color: INK2 }}>{fmtDate(m.date)}</span>
                        </span>
                    </div>
                    <div
                        style={{
                            fontSize: 12.5,
                            color: INK,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {m.subject || "Kein Betreff"}
                    </div>
                    {m.preview && (
                        <div
                            style={{
                                fontSize: 12,
                                color: INK2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {m.preview}
                        </div>
                    )}
                </Zeile>
                );
            })}
        </>
    );
};
